import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#census", label: "Family" },
  { href: "#services", label: "Services" },
  { href: "#gallery", label: "Gallery" },
  { href: "#real-rescues", label: "Rescues" },
  { href: "#stories", label: "Stories" },
  { href: "#volunteer", label: "Volunteer" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E3DECE] shadow-[0_1px_0_0_rgba(0,0,0,0.02)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="#home"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative w-12 h-12 rounded-full bg-white border border-[#E3DECE] overflow-hidden flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/883uiflg_image.png"
              alt="Hastkesar Panjrapole logo"
              className="w-full h-full object-contain p-0.5"
            />
          </span>
          <span className="leading-tight">
            <span className={`block font-display text-lg ${scrolled ? "text-[#1B4D3E]" : "text-[#1B4D3E]"}`}>Hastkesar Panjrapole</span>
            <span className={`block text-[10px] tracking-[0.28em] uppercase ${scrolled ? "text-[#7A8C81]" : "text-[#7A8C81]"}`}>
              Bokadia Family
            </span>
          </span>
        </a>

        <ul className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`navlink-${l.label.toLowerCase()}`}
                className="text-sm text-[#1A2E24] hover:text-[#1B4D3E] transition-colors relative after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-0 after:bg-[#D4AF37] hover:after:w-full after:transition-all"
              >
                {l.label}
              </a>
            </li>
          ))}
          <li>
            <Link
              to="/apan-ghar"
              data-testid="navlink-apan-ghar"
              className="text-sm text-[#5C3A1F] hover:text-[#C9A66B] transition-colors italic font-display text-[15px] relative after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-0 after:bg-[#5C3A1F] hover:after:w-full after:transition-all"
            >
              Apna Ghar →
            </Link>
          </li>
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="#contact"
            data-testid="navbar-emergency-btn"
            className="text-sm text-[#1B4D3E] font-medium flex items-center gap-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#D4AF37]" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]" />
            </span>
            Emergency
          </a>
          <a
            href="tel:+918141646161"
            data-testid="navbar-call-btn"
            className="group inline-flex items-center gap-2 bg-[#1B4D3E] hover:bg-[#143C30] text-[#FDFBF7] px-5 py-2.5 rounded-full text-sm font-medium transition-all"
          >
            <Heart className="w-4 h-4 text-[#D4AF37] group-hover:scale-110 transition-transform" />
            Call Us
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[#E3DECE]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-[#E3DECE]">
          <ul className="px-6 py-6 space-y-4">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`navlink-mobile-${l.label.toLowerCase()}`}
                  className="block text-[#1A2E24] text-base"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/apan-ghar"
                onClick={() => setOpen(false)}
                data-testid="navlink-mobile-apan-ghar"
                className="block text-[#5C3A1F] italic font-display text-lg"
              >
                Apna Ghar →
              </Link>
            </li>
            <li className="pt-2">
              <a
                href="tel:+918141646161"
                onClick={() => setOpen(false)}
                data-testid="navbar-mobile-call-btn"
                className="block text-center bg-[#1B4D3E] text-[#FDFBF7] px-5 py-3 rounded-full"
              >
                Call Helpline
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
